// Database service – Supabase (no localStorage)
import { supabase } from '../lib/supabase';

const TABLE = {
  LEADS: 'myr_leads',
  SCORES: 'myr_scores',
  EVENTS: 'myr_events',
  HERO_CONTENT: 'myr_hero_content',
  ABOUT_CONTENT: 'myr_about_content',
  RESOURCES_CONTENT: 'myr_resources_content',
  SITE_SETTINGS: 'myr_site_settings',
  CALCULATOR_RESULTS: 'myr_calculator_results',
  NEWSLETTER: 'myr_newsletter',
};

// Defaults when no row exists
const DEFAULT_HERO = {
  titleEs: 'Construye Riqueza y Legado',
  titleEn: 'Build Wealth and Legacy',
  subtitleEs: 'Empoderando a mujeres emprendedoras para dominar sus finanzas y crear un impacto global.',
  subtitleEn: 'Empowering women entrepreneurs to master their finances and create global impact.',
  backgroundImage: 'https://images.unsplash.com/photo-1573164713988-8665fc963095?auto=format&fit=crop&q=80&w=1000',
  statsNumber: '5,000+',
  statsTextEs: 'Mujeres empoderadas en nuestra comunidad global.',
  statsTextEn: 'Women empowered in our global community.',
};

const DEFAULT_ABOUT = {
  photo: 'https://media.publit.io/file/profile-photos/Jenny-photo.jpg',
  title: 'Founder, Mujeres y Riquezas',
  quoteEs: 'Mi misión es simple: poner más dinero en manos de mujeres con buen corazón. Cuando una mujer construye riqueza, toda su comunidad prospera.',
  quoteEn: 'My mission is simple: put more money in the hands of good-hearted women. When a woman builds wealth, her entire community prospers.',
  bioEs: 'Fundadora del movimiento Mujeres y Riquezas y Ejecutiva de Prosperity Leaders. Jenny se dedica a cerrar la brecha de riqueza para las mujeres latinas a través de la educación financiera y el emprendimiento.',
  bioEn: 'Founder of the Mujeres y Riquezas movement and Executive at Prosperity Leaders. Jenny is dedicated to closing the wealth gap for Latinas through financial education and entrepreneurship.',
  mediaLogos: [],
};

const DEFAULT_EVENT = {
  titleEs: 'Masterclass: Finanzas para Emprendedoras',
  titleEn: 'Masterclass: Finance for Women Entrepreneurs',
  date: '2024-06-15',
  time: '18:00',
  timezone: 'EST',
  location: 'Miami, FL & Online',
  descriptionEs: 'Aprende a estructurar tu negocio para el crecimiento masivo.',
  descriptionEn: 'Learn to structure your business for massive growth.',
  bannerImage: '',
  registrationUrl: 'https://ProsperityMentors.net',
  visible: true,
};

const DEFAULT_SETTINGS = {
  siteTitleEs: 'Mujeres y Riquezas',
  siteTitleEn: 'Women and Wealth',
  taglineEs: 'Empoderando libertad financiera',
  taglineEn: 'Empowering financial freedom',
  contactEmail: 'contact@mujeresyriquezas.com',
  contactPhone: '',
  instagram: '#',
  facebook: '#',
  linkedin: '#',
  youtube: '',
  tiktok: '',
  footerTaglineEs: 'Empoderando libertad financiera',
  footerTaglineEn: 'Empowering financial freedom',
  copyrightText: 'Todos los derechos reservados.',
  privacyUrl: '#',
  termsUrl: '#',
  enableScorecard: true,
  enableCalculator: true,
  enableResources: true,
  enableEvents: true,
  enableNewsletter: true,
};

const DEFAULT_RESOURCES = [
  { id: 1, icon: 'Book', titleEs: '10 Estrategias para Construir Riqueza', titleEn: '10 Strategies to Build Wealth', descEs: 'Guía PDF esencial.', descEn: 'Essential PDF guide.', pdfFile: null, pdfFileName: '', active: true, order: 1 },
  { id: 2, icon: 'TrendingUp', titleEs: 'Guía de Inversión para Emprendedoras', titleEn: 'Investment Guide for Entrepreneurs', descEs: 'Ebook completo.', descEn: 'Complete Ebook.', pdfFile: null, pdfFileName: '', active: true, order: 2 },
  { id: 3, icon: 'CheckSquare', titleEs: 'Lista de Reducción de Impuestos', titleEn: 'Tax Reduction Checklist', descEs: 'Checklist práctica.', descEn: 'Practical checklist.', pdfFile: null, pdfFileName: '', active: true, order: 3 },
  { id: 4, icon: 'Edit3', titleEs: 'Planificación de Legado', titleEn: 'Legacy Planning', descEs: 'Workbook interactivo.', descEn: 'Interactive workbook.', pdfFile: null, pdfFileName: '', active: true, order: 4 },
];

// Map DB snake_case row to app camelCase (for single-row content)
function heroRowToApp(row) {
  if (!row) return null;
  return {
    titleEs: row.title_es,
    titleEn: row.title_en,
    subtitleEs: row.subtitle_es,
    subtitleEn: row.subtitle_en,
    backgroundImage: row.background_image,
    statsNumber: row.stats_number,
    statsTextEs: row.stats_text_es,
    statsTextEn: row.stats_text_en,
  };
}

function heroAppToRow(app) {
  return {
    id: 1,
    title_es: app.titleEs,
    title_en: app.titleEn,
    subtitle_es: app.subtitleEs,
    subtitle_en: app.subtitleEn,
    background_image: app.backgroundImage,
    stats_number: app.statsNumber,
    stats_text_es: app.statsTextEs,
    stats_text_en: app.statsTextEn,
  };
}

function aboutRowToApp(row) {
  if (!row) return null;
  return {
    photo: row.photo,
    title: row.title,
    quoteEs: row.quote_es,
    quoteEn: row.quote_en,
    bioEs: row.bio_es,
    bioEn: row.bio_en,
    mediaLogos: row.media_logos ?? [],
  };
}

function aboutAppToRow(app) {
  return {
    id: 1,
    photo: app.photo,
    title: app.title,
    quote_es: app.quoteEs,
    quote_en: app.quoteEn,
    bio_es: app.bioEs,
    bio_en: app.bioEn,
    media_logos: app.mediaLogos ?? [],
  };
}

function eventRowToApp(row) {
  if (!row) return null;
  return {
    titleEs: row.title_es,
    titleEn: row.title_en,
    date: row.date,
    time: row.time,
    timezone: row.timezone,
    location: row.location,
    descriptionEs: row.description_es,
    descriptionEn: row.description_en,
    bannerImage: row.banner_image,
    registrationUrl: row.registration_url,
    visible: row.visible ?? true,
  };
}

function eventAppToRow(app) {
  return {
    id: 1,
    title_es: app.titleEs,
    title_en: app.titleEn,
    date: app.date,
    time: app.time,
    timezone: app.timezone,
    location: app.location,
    description_es: app.descriptionEs,
    description_en: app.descriptionEn,
    banner_image: app.bannerImage,
    registration_url: app.registrationUrl,
    visible: app.visible ?? true,
  };
}

function settingsRowToApp(row) {
  if (!row) return null;
  return {
    siteTitleEs: row.site_title_es,
    siteTitleEn: row.site_title_en,
    taglineEs: row.tagline_es,
    taglineEn: row.tagline_en,
    contactEmail: row.contact_email,
    contactPhone: row.contact_phone,
    instagram: row.instagram,
    facebook: row.facebook,
    linkedin: row.linkedin,
    youtube: row.youtube,
    tiktok: row.tiktok,
    footerTaglineEs: row.footer_tagline_es,
    footerTaglineEn: row.footer_tagline_en,
    copyrightText: row.copyright_text,
    privacyUrl: row.privacy_url,
    termsUrl: row.terms_url,
    enableScorecard: row.enable_scorecard ?? true,
    enableCalculator: row.enable_calculator ?? true,
    enableResources: row.enable_resources ?? true,
    enableEvents: row.enable_events ?? true,
    enableNewsletter: row.enable_newsletter ?? true,
  };
}

function settingsAppToRow(app) {
  return {
    id: 1,
    site_title_es: app.siteTitleEs,
    site_title_en: app.siteTitleEn,
    tagline_es: app.taglineEs,
    tagline_en: app.taglineEn,
    contact_email: app.contactEmail,
    contact_phone: app.contactPhone,
    instagram: app.instagram,
    facebook: app.facebook,
    linkedin: app.linkedin,
    youtube: app.youtube,
    tiktok: app.tiktok,
    footer_tagline_es: app.footerTaglineEs,
    footer_tagline_en: app.footerTaglineEn,
    copyright_text: app.copyrightText,
    privacy_url: app.privacyUrl,
    terms_url: app.termsUrl,
    enable_scorecard: app.enableScorecard ?? true,
    enable_calculator: app.enableCalculator ?? true,
    enable_resources: app.enableResources ?? true,
    enable_events: app.enableEvents ?? true,
    enable_newsletter: app.enableNewsletter ?? true,
  };
}

function resourceRowToApp(row) {
  if (!row) return null;
  return {
    id: row.id,
    icon: row.icon,
    titleEs: row.title_es,
    titleEn: row.title_en,
    descEs: row.desc_es,
    descEn: row.desc_en,
    pdfFile: row.pdf_file,
    pdfFileName: row.pdf_file_name,
    active: row.active ?? true,
    order: row.order ?? 0,
  };
}

function resourceAppToRow(app) {
  return {
    icon: app.icon,
    title_es: app.titleEs,
    title_en: app.titleEn,
    desc_es: app.descEs,
    desc_en: app.descEn,
    pdf_file: app.pdfFile,
    pdf_file_name: app.pdfFileName,
    active: app.active ?? true,
    order: app.order ?? 0,
  };
}

export const db = {
  async saveLead(email, source) {
    const { data, error } = await supabase.from(TABLE.LEADS).insert({ email, source }).select().single();
    if (error) throw error;
    return { id: data.id, email: data.email, source: data.source, date: data.date };
  },

  async getLeads() {
    const { data, error } = await supabase.from(TABLE.LEADS).select('*').order('date', { ascending: false });
    if (error) throw error;
    return (data || []).map((r) => ({ id: r.id, email: r.email, source: r.source, date: r.date }));
  },

  async deleteLead(id) {
    const { error } = await supabase.from(TABLE.LEADS).delete().eq('id', id);
    if (error) throw error;
    return true;
  },

  async saveScore(email, score, breakdown) {
    await this.saveLead(email, 'Scorecard');
    const { data, error } = await supabase.from(TABLE.SCORES).insert({ email, score, breakdown }).select().single();
    if (error) throw error;
    return { id: data.id, email: data.email, score: data.score, breakdown: data.breakdown, date: data.date };
  },

  async getScores() {
    const { data, error } = await supabase.from(TABLE.SCORES).select('*').order('date', { ascending: false });
    if (error) throw error;
    return (data || []).map((r) => ({ id: r.id, email: r.email, score: r.score, breakdown: r.breakdown, date: r.date }));
  },

  async deleteScore(id) {
    const { error } = await supabase.from(TABLE.SCORES).delete().eq('id', id);
    if (error) throw error;
    return true;
  },

  async getEvent() {
    const { data, error } = await supabase.from(TABLE.EVENTS).select('*').eq('id', 1).maybeSingle();
    if (error) throw error;
    const app = eventRowToApp(data);
    return app ? { ...DEFAULT_EVENT, ...app } : DEFAULT_EVENT;
  },

  async updateEvent(eventData) {
    const row = eventAppToRow(eventData);
    const { error } = await supabase.from(TABLE.EVENTS).upsert(row, { onConflict: 'id' });
    if (error) throw error;
    return eventData;
  },

  async getHeroContent() {
    const { data, error } = await supabase.from(TABLE.HERO_CONTENT).select('*').eq('id', 1).maybeSingle();
    if (error) throw error;
    const app = heroRowToApp(data);
    return app ? { ...DEFAULT_HERO, ...app } : DEFAULT_HERO;
  },

  async updateHeroContent(data) {
    const row = heroAppToRow(data);
    const { error } = await supabase.from(TABLE.HERO_CONTENT).upsert(row, { onConflict: 'id' });
    if (error) throw error;
    return data;
  },

  async getAboutContent() {
    const { data, error } = await supabase.from(TABLE.ABOUT_CONTENT).select('*').eq('id', 1).maybeSingle();
    if (error) throw error;
    const app = aboutRowToApp(data);
    return app ? { ...DEFAULT_ABOUT, ...app } : DEFAULT_ABOUT;
  },

  async updateAboutContent(data) {
    const row = aboutAppToRow(data);
    const { error } = await supabase.from(TABLE.ABOUT_CONTENT).upsert(row, { onConflict: 'id' });
    if (error) throw error;
    return data;
  },

  async getResourcesContent() {
    const { data, error } = await supabase.from(TABLE.RESOURCES_CONTENT).select('*').order('order', { ascending: true });
    if (error) throw error;
    if (!data || data.length === 0) return DEFAULT_RESOURCES;
    return data.map(resourceRowToApp);
  },

  async updateResourcesContent(items) {
    if (!items || items.length === 0) return items;
    const rows = items.map((item) => {
      const row = resourceAppToRow(item);
      if (item.id != null) row.id = item.id;
      return row;
    });
    const { data, error } = await supabase.from(TABLE.RESOURCES_CONTENT).upsert(rows, { onConflict: 'id' }).select();
    if (error) throw error;
    return (data || []).map(resourceRowToApp);
  },

  async getSiteSettings() {
    const { data, error } = await supabase.from(TABLE.SITE_SETTINGS).select('*').eq('id', 1).maybeSingle();
    if (error) throw error;
    const app = settingsRowToApp(data);
    return app ? { ...DEFAULT_SETTINGS, ...app } : DEFAULT_SETTINGS;
  },

  async updateSiteSettings(data) {
    const row = settingsAppToRow(data);
    const { error } = await supabase.from(TABLE.SITE_SETTINGS).upsert(row, { onConflict: 'id' });
    if (error) throw error;
    return data;
  },

  async saveCalculatorResult(email, income, investRate, projections) {
    await this.saveLead(email, 'Calculator');
    const { data, error } = await supabase
      .from(TABLE.CALCULATOR_RESULTS)
      .insert({ email, income, invest_rate: investRate, projections })
      .select()
      .single();
    if (error) throw error;
    return {
      id: data.id,
      email: data.email,
      income: data.income,
      investRate: data.invest_rate,
      projections: data.projections,
      date: data.date,
    };
  },

  async getCalculatorResults() {
    const { data, error } = await supabase.from(TABLE.CALCULATOR_RESULTS).select('*').order('date', { ascending: false });
    if (error) throw error;
    return (data || []).map((r) => ({
      id: r.id,
      email: r.email,
      income: r.income,
      investRate: r.invest_rate,
      projections: r.projections,
      date: r.date,
    }));
  },

  async deleteCalculatorResult(id) {
    const { error } = await supabase.from(TABLE.CALCULATOR_RESULTS).delete().eq('id', id);
    if (error) throw error;
    return true;
  },

  async saveNewsletter(email) {
    await this.saveLead(email, 'Newsletter');
    const { data, error } = await supabase.from(TABLE.NEWSLETTER).insert({ email }).select().single();
    if (error) throw error;
    return { id: data.id, email: data.email, date: data.date };
  },

  async getNewsletterSubscribers() {
    const { data, error } = await supabase.from(TABLE.NEWSLETTER).select('*').order('date', { ascending: false });
    if (error) throw error;
    return (data || []).map((r) => ({ id: r.id, email: r.email, date: r.date }));
  },

  async deleteNewsletterSubscriber(id) {
    const { error } = await supabase.from(TABLE.NEWSLETTER).delete().eq('id', id);
    if (error) throw error;
    return true;
  },
};
